import { Reveal } from "./lib";
import { IMG, planet } from "./content";
import { Divider } from "./Chrome";
import { HighlightsPanel } from "./parts";
import { GhgChart, RenewableChart, Scope3Chart, WaterChart } from "./PlanetCharts";
import { VIDEOS, VideoFeature } from "./Video";

type Goal = { goal: string; progress: string; status: string };

function StatusIcon({ status }: { status: string }) {
  const on = status === "on";
  return (
    <span className="inline-flex items-center gap-2 text-xs font-semibold">
      <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden>
        <circle cx="11" cy="11" r="9.5" fill="none" stroke="#53565a" strokeWidth="1.5" />
        {on ? <circle cx="11" cy="11" r="7" fill="#53565a" /> : <path d="M11 3.5a7.5 7.5 0 0 1 0 15z" fill="#53565a" />}
      </svg>
      {on ? "On target" : "In progress"}
    </span>
  );
}

function GoalTable({ title, goals, testId }: { title: string; goals: Goal[]; testId: string }) {
  return (
    <Reveal className="overflow-hidden" testId={testId}>
      <h4 className="am-bg-accent px-4 py-3 text-base font-semibold text-white">{title}</h4>
      <table className="w-full text-left text-sm">
        <caption className="sr-only">{title}</caption>
        <thead className="hidden sm:table-header-group">
          <tr className="border-b-4 border-[var(--am-mist)]">
            <th scope="col" className="w-[40%] px-4 py-3 font-semibold text-[#2b2d30]">Goal</th>
            <th scope="col" className="px-4 py-3 font-semibold text-[#2b2d30]">Progress*</th>
            <th scope="col" className="w-[110px] px-4 py-3 font-semibold text-[#2b2d30]">Status</th>
          </tr>
        </thead>
        <tbody>
          {goals.map((g, i) => (
            <tr key={g.goal} className="block border-b-4 border-[var(--am-mist)] sm:table-row" data-testid={`${testId}-row-${i}`}>
              <td className="block px-4 pt-4 sm:table-cell sm:py-4 sm:align-top">{g.goal}</td>
              <td className="block px-4 pt-2 am-accent sm:table-cell sm:py-4 sm:align-top">{g.progress}</td>
              <td className="block px-4 py-3 sm:table-cell sm:py-4 sm:align-top"><StatusIcon status={g.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Reveal>
  );
}

export function PlanetSection() {
  return (
    <section id="planet" className="am-planet scroll-mt-28" data-testid="am-section-planet">
      <Divider id="planet" number="04" title="Environmental Progress"
        items={["2025 Highlights", "Goals and Progress", "Progress on GHG Emissions", "Net Zero 2040 Playbook™", "Product Efficiency", "Supply Chain Emissions", "Circular Economy", "Waste Management", "Water Management"]}
        image="p41_0.jpg" alt="Hands holding soil above leafy crops next to a tablet showing charts" />
      <div className="mx-auto max-w-[1320px] px-5 py-20 sm:px-8 sm:py-28">
        <div className="grid gap-12 lg:grid-cols-[1fr_420px]">
          <div>
            <Reveal><p className="am-headline text-2xl sm:text-3xl lg:text-4xl" data-testid="am-planet-headline">{planet.headline}</p></Reveal>
            <div className="mt-10 grid gap-6 text-[15px] leading-relaxed sm:grid-cols-2">
              {planet.intro.map((p) => <Reveal key={p.slice(0, 20)}><p>{p}</p></Reveal>)}
              <Reveal className="am-photo am-clip aspect-[3/2]"><img src={IMG("p52_0.jpg")} alt="A semiconductor equipment cleanroom" className="h-full w-full object-cover" loading="lazy" /></Reveal>
            </div>
          </div>
          <HighlightsPanel items={planet.highlights} page={42} prefix="am-planet" />
        </div>
        <VideoFeature video={VIDEOS.planet} testId="am-video-planet" />

        <div className="mt-24 flex flex-wrap items-end justify-between gap-4">
          <h3 className="am-title text-4xl sm:text-5xl">Goals and Progress</h3>
        </div>
        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          <GoalTable title="Scope 1, 2 and 3 Science-Based Targets" goals={planet.goals.sbt} testId="am-goals-sbt" />
          <GoalTable title="3x30 Goals" goals={planet.goals.x30} testId="am-goals-3x30" />
        </div>
        <p className="mt-4 text-xs">*Cumulative through FY 2025 unless otherwise noted. Status as shown in the report.</p>

        <h3 className="mt-24 text-2xl font-semibold am-accent sm:text-3xl">Progress on GHG emissions</h3>
        <p className="mt-3 max-w-3xl text-[15px] leading-relaxed">Applied is advancing toward achieving our 2030 emissions goals, validated by the Science Based Targets initiative (SBTi) as aligned with the 1.5°C pathway.</p>
        <div className="mt-8 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <GhgChart />
          <Scope3Chart />
        </div>
        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1fr_0.8fr]">
          <RenewableChart />
          <WaterChart />
          <Reveal className="am-photo am-clip min-h-[260px]"><img src={IMG("p58_0.jpg")} alt="Cooling towers at an Applied Materials facility" className="h-full w-full object-cover" loading="lazy" /></Reveal>
        </div>
      </div>
    </section>
  );
}
